import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Store, Image as ImageIcon, Upload, Loader2, ShieldCheck, Phone,
  MapPin, FileText, CreditCard, AlertTriangle, CheckCircle2, ExternalLink,
  Settings, MessageCircle, Variable, RotateCcw, Eye, ShoppingBag, ChevronRight,
} from "lucide-react";
import {
  GATILHOS, VARIAVEIS_DISPONIVEIS, TEMPLATES_PADRAO, BOTOES_PADRAO,
  renderTemplate, limparCacheMensagens,
} from "../lib/whatsappTemplates";
import LojaOnlinePage from "./LojaOnlinePage";

const API = `${window.location.origin}/api`;

const PREVIEW_VARS = {
  nome_vendedor: "Carlos Silva",
  nome_loja: "Minha Loja Demo",
  nome_cliente: "Maria Souza",
  primeiro_nome: "Maria",
  numero_pedido: "1234",
  valor_total: "R$ 450,00",
  saldo_devedor: "R$ 200,00",
  data_compra: "10/05/2026",
  data_vencimento: "10/06/2026",
  atraso_dias: "7",
  link_pagamento: "https://sua-loja.com/loja",
  link_segunda_via: "https://sua-loja.com/api/promissorias/abc/pdf-publico?token=xyz",
  link_perfil_cliente: "https://sua-loja.com/loja",
  link_loja: "https://sua-loja.com/loja",
};

const SECOES = [
  { id: "perfil",     label: "Perfil da Loja",      icon: Store,          accent: "emerald", desc: "Dados da sua empresa",
    cls: { sidebarActive: "bg-emerald-100 text-emerald-700", chipActive: "bg-emerald-600", iconBg: "bg-emerald-100 text-emerald-700" } },
  { id: "pagamento",  label: "Pagamento",           icon: CreditCard,     accent: "purple",  desc: "InfinitePay & checkout",
    cls: { sidebarActive: "bg-purple-100 text-purple-700",   chipActive: "bg-purple-600",  iconBg: "bg-purple-100 text-purple-700" } },
  { id: "mensagens",  label: "Mensagens & WhatsApp",icon: MessageCircle,  accent: "blue",    desc: "Templates personalizáveis",
    cls: { sidebarActive: "bg-blue-100 text-blue-700",       chipActive: "bg-blue-600",    iconBg: "bg-blue-100 text-blue-700" } },
  { id: "loja_online",label: "Loja Online",         icon: ShoppingBag,    accent: "fuchsia", desc: "Banners, produtos, tema",
    cls: { sidebarActive: "bg-fuchsia-100 text-fuchsia-700", chipActive: "bg-fuchsia-600", iconBg: "bg-fuchsia-100 text-fuchsia-700" } },
];

export default function ConfiguracoesLojaPage() {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = user.tipo === "admin" || !user.tipo;

  const [secao, setSecao] = useState("perfil");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [perfil, setPerfil] = useState({
    nome_loja: "", cnpj_cpf: "", endereco: "", telefone: "", logo_path: null,
  });
  const [infinitetag, setInfinitetag] = useState("");
  const [mensagens, setMensagens] = useState({});
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { carregar(); }, []);

  const carregar = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/loja/config`);
      const p = data?.perfil_loja || {};
      setPerfil({
        nome_loja: p.nome_loja || user.nome_loja || "",
        cnpj_cpf: p.cnpj_cpf || "",
        endereco: p.endereco || "",
        telefone: p.telefone || "",
        logo_path: p.logo_path || null,
      });
      setInfinitetag(data?.infinitetag || "");
      setMensagens(data?.mensagens_whatsapp || {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao carregar configurações");
    } finally {
      setLoading(false);
    }
  };

  const salvar = async () => {
    if (!isAdmin) return;
    setSaving(true);
    try {
      await axios.put(`${API}/loja/config`, {
        perfil_loja: perfil,
        infinitetag,
        mensagens_whatsapp: mensagens,
      });
      toast.success("Configurações salvas com sucesso!");
      limparCacheMensagens();
      const u = JSON.parse(localStorage.getItem("user") || "{}");
      u.nome_loja = perfil.nome_loja || u.nome_loja;
      localStorage.setItem("user", JSON.stringify(u));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const enviarLogo = async (file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error("Máx 3MB"); return; }
    setUploadingLogo(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/loja/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPerfil((prev) => ({ ...prev, logo_path: data.logo_path }));
      toast.success("Logotipo atualizado!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao enviar logotipo");
    } finally {
      setUploadingLogo(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const logoUrl = perfil.logo_path
    ? `${API}/loja/logo-file/${perfil.logo_path.split("/").pop()}`
    : null;
  const tagOk = !!(infinitetag || "").trim();
  const secaoAtual = SECOES.find((s) => s.id === secao) || SECOES[0];
  const showSaveBar = secao !== "loja_online";

  if (!isAdmin) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-center">
        <ShieldCheck className="h-16 w-16 mx-auto text-amber-500 mb-4" />
        <h2 className="text-2xl font-bold mb-2">Acesso restrito</h2>
        <p className="text-gray-600">Apenas administradores podem editar as configurações.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="configuracoes-loja-page">
      {/* Header com gradiente sutil */}
      <div className="rounded-2xl bg-gradient-to-r from-slate-50 via-blue-50/40 to-emerald-50/40 border border-gray-100 p-6 flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center">
          <Settings className="h-6 w-6 text-blue-600" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "Work Sans" }}>
            Configurações da Loja
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">Gerencie sua empresa, pagamentos, mensagens e loja virtual.</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-gray-500">
          <Loader2 className="h-6 w-6 animate-spin mr-2" /> Carregando…
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
          {/* Sidebar (desktop) / Tabs horizontais (mobile) */}
          <aside className="lg:sticky lg:top-4 lg:self-start">
            {/* Desktop: lista vertical */}
            <nav className="hidden lg:flex flex-col gap-1.5" data-testid="config-sidebar">
              {SECOES.map((s) => {
                const Icon = s.icon;
                const ativo = secao === s.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => setSecao(s.id)}
                    data-testid={`sidebar-item-${s.id}`}
                    className={`flex items-center gap-3 px-3 py-3 rounded-xl text-left transition-all group ${
                      ativo
                        ? "bg-white shadow-sm border border-gray-200"
                        : "hover:bg-white/60 border border-transparent"
                    }`}
                  >
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                      ativo ? s.cls.sidebarActive : "bg-gray-100 text-gray-500 group-hover:bg-gray-200"
                    }`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-semibold ${ativo ? "text-gray-900" : "text-gray-700"}`}>{s.label}</p>
                      <p className="text-[11px] text-gray-500 truncate">{s.desc}</p>
                    </div>
                    {ativo && <ChevronRight className="h-4 w-4 text-gray-400" />}
                  </button>
                );
              })}
            </nav>

            {/* Mobile: chips horizontais */}
            <div className="lg:hidden flex gap-2 overflow-x-auto pb-2 -mx-1 px-1" data-testid="config-tabs-mobile">
              {SECOES.map((s) => {
                const Icon = s.icon;
                const ativo = secao === s.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => setSecao(s.id)}
                    data-testid={`mobile-tab-${s.id}`}
                    className={`shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all ${
                      ativo
                        ? `bg-${s.accent}-600 text-white shadow-md`
                        : "bg-white text-gray-700 border border-gray-200"
                    }`}
                  >
                    <Icon className="h-4 w-4" /> {s.label}
                  </button>
                );
              })}
            </div>
          </aside>

          {/* Conteúdo */}
          <main className="min-w-0 space-y-4">
            <SecaoHeader secao={secaoAtual} />

            {secao === "perfil" && (
              <PainelPerfil
                perfil={perfil}
                setPerfil={setPerfil}
                logoUrl={logoUrl}
                uploadingLogo={uploadingLogo}
                onUpload={enviarLogo}
                fileRef={fileRef}
              />
            )}

            {secao === "pagamento" && (
              <PainelPagamento
                infinitetag={infinitetag}
                setInfinitetag={setInfinitetag}
                tagOk={tagOk}
              />
            )}

            {secao === "mensagens" && (
              <PainelMensagens
                mensagens={mensagens}
                setMensagens={setMensagens}
              />
            )}

            {secao === "loja_online" && (
              <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-6" data-testid="painel-loja-online">
                <LojaOnlinePage embedded />
              </div>
            )}
          </main>
        </div>
      )}

      {/* Barra de salvar (fica oculta em Loja Online porque ela tem botões próprios) */}
      {!loading && showSaveBar && (
        <div className="sticky bottom-4 z-10 flex justify-end">
          <Button
            onClick={salvar}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 gap-2 shadow-lg rounded-full px-6 py-6 text-base"
            data-testid="btn-salvar-config"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            {saving ? "Salvando…" : "Salvar Configurações"}
          </Button>
        </div>
      )}
    </div>
  );
}

// ============== Helpers visuais ==============

function SecaoHeader({ secao }) {
  const Icon = secao.icon;
  return (
    <div className="flex items-center gap-3 lg:hidden">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${secao.cls.iconBg}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <h3 className="text-lg font-bold text-gray-900">{secao.label}</h3>
        <p className="text-xs text-gray-500">{secao.desc}</p>
      </div>
    </div>
  );
}

function Bloco({ titulo, descricao, children, accent = "gray" }) {
  const accentBar = {
    gray:    "bg-gray-300",
    emerald: "bg-emerald-400",
    purple:  "bg-purple-400",
    blue:    "bg-blue-400",
    fuchsia: "bg-fuchsia-400",
  }[accent] || "bg-gray-300";
  return (
    <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" data-testid={`bloco-${titulo?.toString().toLowerCase().replace(/\s+/g,"-")}`}>
      {titulo && (
        <header className="px-5 sm:px-6 py-4 border-b border-gray-100 flex items-start gap-3">
          <div className={`hidden sm:block w-1 self-stretch rounded-full ${accentBar}`} />
          <div className="flex-1">
            <h4 className="text-base font-semibold text-gray-900">{titulo}</h4>
            {descricao && <p className="text-xs text-gray-500 mt-0.5">{descricao}</p>}
          </div>
        </header>
      )}
      <div className="p-5 sm:p-6 space-y-4">{children}</div>
    </section>
  );
}

function CampoLabel({ children, icon: Icon }) {
  return (
    <Label className="flex items-center gap-1.5 text-xs font-medium text-gray-600 mb-1.5">
      {Icon && <Icon className="h-3.5 w-3.5" />} {children}
    </Label>
  );
}

// ============== Painéis ==============

function PainelPerfil({ perfil, setPerfil, logoUrl, uploadingLogo, onUpload, fileRef }) {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const uid = user?.id || "";
  // Versão derivada do timestamp da logo (força WhatsApp a re-buscar preview ao trocar foto)
  const logoVersion = (perfil?.logo_path || "").split("/").pop()?.split(".")[0]?.slice(-8) || "v1";
  // Link "amigável" para o app real (usado em "Abrir loja" e "Ver Loja")
  const linkLoja = uid ? `${window.location.origin}/loja?uid=${uid}` : "";
  // Link de COMPARTILHAMENTO (WhatsApp/Facebook lê OG tags daqui)
  // O ?v= invalida o cache do WhatsApp sempre que a logo é trocada.
  const linkShare = uid ? `${window.location.origin}/api/loja/share/${uid}?v=${logoVersion}` : "";
  const copiarLink = async () => {
    try {
      await navigator.clipboard.writeText(linkShare || linkLoja);
      toast.success("Link copiado!");
    } catch {
      const i = document.querySelector('[data-testid="input-link-loja"]');
      if (i) { i.select(); document.execCommand("copy"); toast.success("Link copiado!"); }
    }
  };
  const compartilharWA = () => {
    const nome = perfil.nome_loja || "minha loja";
    const txt = `🛍️ Acesse ${nome} pelo link: ${linkShare || linkLoja}`;
    window.location.href = `https://wa.me/?text=${encodeURIComponent(txt)}`;
  };
  return (
    <Bloco titulo="Identidade da empresa" descricao="Dados públicos exibidos na loja virtual, no ícone do app instalado, no header do painel e em PDFs." accent="emerald">
      {/* Link exclusivo da loja do assinante */}
      <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-4 space-y-2" data-testid="bloco-link-loja">
        <div className="flex items-center gap-2">
          <ExternalLink className="h-4 w-4 text-blue-700" />
          <p className="text-sm font-semibold text-blue-900">Link exclusivo da sua loja</p>
        </div>
        <p className="text-xs text-blue-800">
          Compartilhe este link com seus clientes. Cada assinante tem o seu — assim seus clientes nunca vão parar na loja de outra empresa.
        </p>
        <div className="flex gap-2">
          <Input
            value={linkShare || linkLoja}
            readOnly
            onFocus={(e) => e.target.select()}
            className="font-mono text-xs bg-white"
            data-testid="input-link-loja"
          />
          <Button type="button" size="sm" variant="outline" onClick={copiarLink} className="shrink-0 gap-1.5 rounded-full" data-testid="btn-copiar-link">
            📋 Copiar
          </Button>
        </div>
        <div className="flex gap-2 pt-1">
          <Button type="button" size="sm" onClick={compartilharWA} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 rounded-full text-xs" data-testid="btn-compartilhar-wa-loja">
            💬 Compartilhar no WhatsApp
          </Button>
          {linkLoja && (
            <a href={linkLoja} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-700 underline self-center" data-testid="link-ver-loja">
              Abrir loja →
            </a>
          )}
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-5 items-start">
        <div className="w-28 h-28 rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden shrink-0">
          {logoUrl
            ? <img src={logoUrl} alt="Logo" className="w-full h-full object-contain" />
            : <ImageIcon className="h-10 w-10 text-gray-300" />}
        </div>
        <div className="flex-1 w-full">
          <CampoLabel>Logotipo</CampoLabel>
          <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-2.5 text-[11px] text-emerald-900 mb-3 space-y-0.5" data-testid="logo-dicas">
            <p className="font-bold flex items-center gap-1.5">📐 Tamanho ideal para encaixar perfeito</p>
            <p>• <strong>512 × 512 pixels</strong> (quadrada — proporção 1:1)</p>
            <p>• Formato <strong>PNG com fundo transparente</strong> de preferência</p>
            <p>• Até 3MB · Imagens fora dessa proporção podem ficar cortadas ao virar ícone do app</p>
            <p className="text-emerald-700 italic mt-1">Onde aparece: loja virtual, ícone do app instalado no celular/PC, header do painel e PDF A4 das promissórias.</p>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/webp"
            className="hidden"
            data-testid="input-logo"
            onChange={(e) => onUpload(e.target.files?.[0])}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-2 rounded-full"
            disabled={uploadingLogo}
            onClick={() => fileRef.current?.click()}
            data-testid="btn-upload-logo"
          >
            {uploadingLogo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {uploadingLogo ? "Enviando…" : logoUrl ? "Trocar logotipo" : "Enviar logotipo"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <CampoLabel icon={Store}>Nome da Loja *</CampoLabel>
          <Input
            value={perfil.nome_loja}
            onChange={(e) => setPerfil({ ...perfil, nome_loja: e.target.value })}
            placeholder="Ex: Dal Cotone Distribuidora"
            data-testid="input-nome-loja"
          />
        </div>
        <div>
          <CampoLabel icon={FileText}>CNPJ / CPF</CampoLabel>
          <Input
            value={perfil.cnpj_cpf}
            onChange={(e) => setPerfil({ ...perfil, cnpj_cpf: e.target.value })}
            placeholder="Ex: 12.345.678/0001-90"
            data-testid="input-cnpj-cpf"
          />
        </div>
      </div>
      <div>
        <CampoLabel icon={MapPin}>Endereço Completo</CampoLabel>
        <Textarea
          value={perfil.endereco}
          onChange={(e) => setPerfil({ ...perfil, endereco: e.target.value })}
          placeholder="Rua, número, bairro, cidade — Estado, CEP"
          rows={2}
          data-testid="input-endereco"
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <CampoLabel icon={Phone}>Telefone de Contato</CampoLabel>
          <Input
            value={perfil.telefone}
            onChange={(e) => setPerfil({ ...perfil, telefone: e.target.value })}
            placeholder="(67) 99999-1234"
            data-testid="input-telefone"
          />
        </div>
      </div>
    </Bloco>
  );
}

function PainelPagamento({ infinitetag, setInfinitetag, tagOk }) {
  return (
    <Bloco titulo="InfinitePay" descricao="Conta usada para receber os pagamentos da loja virtual." accent="purple">
      <div className={`rounded-xl p-4 text-sm flex gap-3 items-start ${
        tagOk ? "bg-emerald-50 text-emerald-900 border border-emerald-200"
              : "bg-amber-50 text-amber-900 border border-amber-200"
      }`}>
        {tagOk ? <CheckCircle2 className="h-5 w-5 mt-0.5 shrink-0 text-emerald-600" />
               : <AlertTriangle className="h-5 w-5 mt-0.5 shrink-0 text-amber-600" />}
        <div>
          {tagOk ? (
            <p><strong>Pagamentos ativos.</strong> Os links de checkout usarão <strong>@{infinitetag.trim()}</strong>.</p>
          ) : (
            <p><strong>Pagamentos indisponíveis.</strong> Insira sua InfiniteTag para que os pagamentos da loja virtual caiam direto na sua conta.</p>
          )}
        </div>
      </div>
      <div>
        <CampoLabel>InfiniteTag</CampoLabel>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-semibold">@</span>
          <Input
            value={infinitetag}
            onChange={(e) => setInfinitetag(e.target.value.replace(/^@+/, "").toLowerCase())}
            placeholder="sua-tag-aqui"
            className="pl-7"
            data-testid="input-infinitetag"
          />
        </div>
        <p className="text-[11px] text-gray-500 mt-2">
          Encontre no app InfinitePay → Perfil.{" "}
          <a href="https://infinitepay.io" target="_blank" rel="noopener noreferrer" className="text-blue-600 inline-flex items-center gap-0.5 hover:underline">
            infinitepay.io <ExternalLink className="h-3 w-3" />
          </a>
        </p>
      </div>
    </Bloco>
  );
}

function PainelMensagens({ mensagens, setMensagens }) {
  return (
    <>
      <div className="bg-blue-50/60 border border-blue-100 rounded-2xl p-4 text-sm text-blue-900 flex gap-3" data-testid="aba-mensagens-conteudo">
        <MessageCircle className="h-5 w-5 mt-0.5 shrink-0 text-blue-600" />
        <p>Personalize as mensagens enviadas via WhatsApp. Deixe em branco para usar o texto padrão. Use o botão <em>Inserir variável</em> para dados dinâmicos.</p>
      </div>
      <div className="space-y-4">
        {GATILHOS.map((g) => (
          <GatilhoEditor
            key={g.id}
            gatilho={g}
            valor={mensagens[g.id] || {}}
            onChange={(novo) => setMensagens((prev) => ({ ...prev, [g.id]: novo }))}
          />
        ))}
      </div>
    </>
  );
}

// ============== Editor de cada gatilho ==============

function GatilhoEditor({ gatilho, valor, onChange }) {
  const [showPreview, setShowPreview] = useState(false);
  const textareaRef = useRef(null);
  const botao = valor.botao || "";
  const template = valor.template || "";

  const inserirVariavel = (token) => {
    const ta = textareaRef.current;
    if (!ta) { onChange({ ...valor, template: (template || "") + token }); return; }
    const start = ta.selectionStart ?? template.length;
    const end = ta.selectionEnd ?? template.length;
    const novo = template.slice(0, start) + token + template.slice(end);
    onChange({ ...valor, template: novo });
    setTimeout(() => {
      try { ta.focus(); ta.selectionStart = ta.selectionEnd = start + token.length; } catch {}
    }, 0);
  };

  const usarPadrao = () => {
    if (template.trim() === "" && botao.trim() === "") return;
    if (!window.confirm("Restaurar o texto padrão deste gatilho?")) return;
    onChange({ botao: "", template: "" });
  };

  const templateEfetivo = template.trim() || TEMPLATES_PADRAO[gatilho.id] || "";
  const botaoEfetivo = botao.trim() || BOTOES_PADRAO[gatilho.id] || "WhatsApp";
  const preview = renderTemplate(templateEfetivo, PREVIEW_VARS);
  const usandoPadrao = !template.trim() && !botao.trim();

  return (
    <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" data-testid={`gatilho-${gatilho.id}`}>
      <header className="px-5 sm:px-6 py-4 border-b border-gray-100 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center">
            <MessageCircle className="h-4 w-4" />
          </div>
          <div>
            <h4 className="text-sm font-semibold text-gray-900">{gatilho.label}</h4>
            {usandoPadrao && <span className="text-[10px] bg-gray-100 text-gray-600 rounded-full px-2 py-0.5 font-medium">Usando padrão</span>}
          </div>
        </div>
        <div className="flex gap-1.5">
          <Button type="button" variant="ghost" size="sm" onClick={() => setShowPreview(!showPreview)} className="gap-1.5 text-xs rounded-full" data-testid={`btn-preview-${gatilho.id}`}>
            <Eye className="h-3.5 w-3.5" /> {showPreview ? "Ocultar" : "Preview"}
          </Button>
          {!usandoPadrao && (
            <Button type="button" variant="ghost" size="sm" onClick={usarPadrao} className="gap-1.5 text-xs rounded-full text-amber-700" data-testid={`btn-padrao-${gatilho.id}`}>
              <RotateCcw className="h-3.5 w-3.5" /> Usar padrão
            </Button>
          )}
        </div>
      </header>

      <div className="p-5 sm:p-6 space-y-3">
        <div>
          <CampoLabel>Texto do botão (no app)</CampoLabel>
          <Input
            value={botao}
            onChange={(e) => onChange({ ...valor, botao: e.target.value })}
            placeholder={`Padrão: "${BOTOES_PADRAO[gatilho.id]}"`}
            maxLength={40}
            data-testid={`input-botao-${gatilho.id}`}
          />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label className="text-xs font-medium text-gray-600">Template da mensagem</Label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="outline" size="sm" className="gap-1.5 text-xs h-7 rounded-full" data-testid={`btn-variaveis-${gatilho.id}`}>
                  <Variable className="h-3.5 w-3.5" /> Inserir variável
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-72 max-h-80 overflow-y-auto">
                <DropdownMenuLabel className="text-xs">Clique para inserir</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {VARIAVEIS_DISPONIVEIS.map((v) => (
                  <DropdownMenuItem
                    key={v.token}
                    onClick={() => inserirVariavel(v.token)}
                    className="cursor-pointer"
                    data-testid={`var-${v.token.replace(/[{}]/g, "")}-${gatilho.id}`}
                  >
                    <div className="flex flex-col w-full">
                      <code className="text-[11px] font-mono text-blue-700">{v.token}</code>
                      <span className="text-[10px] text-gray-500">{v.desc}</span>
                    </div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <Textarea
            ref={textareaRef}
            value={template}
            onChange={(e) => onChange({ ...valor, template: e.target.value })}
            placeholder={TEMPLATES_PADRAO[gatilho.id]}
            rows={8}
            className="font-mono text-sm bg-gray-50/60 focus:bg-white"
            data-testid={`textarea-template-${gatilho.id}`}
          />
          <p className="text-[10px] text-gray-500 mt-1">Em branco = usa o texto padrão.</p>
        </div>
        {showPreview && (
          <div className="rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50/70 to-white p-4" data-testid={`preview-${gatilho.id}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] uppercase font-bold text-emerald-700 tracking-wider">Preview</span>
              <span className="text-[10px] text-gray-500">Botão: <strong>{botaoEfetivo}</strong></span>
            </div>
            <pre className="whitespace-pre-wrap text-xs text-gray-800 font-sans leading-relaxed">{preview}</pre>
          </div>
        )}
      </div>
    </section>
  );
}
